/**
 * Map a Firebase Auth error to a human-readable message.
 *
 * Firebase surfaces failures as `FirebaseError` with a stable `code`
 * (e.g. `auth/invalid-credential`). We translate the common ones to friendly
 * copy and fall back to the supplied default for everything else.
 */

const MESSAGES: Record<string, string> = {
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/user-disabled': 'This account has been disabled.',
  'auth/user-not-found': 'No account found with that email.',
  'auth/wrong-password': 'Invalid email or password.',
  'auth/invalid-credential': 'Invalid email or password.',
  'auth/email-already-in-use': 'An account with that email already exists.',
  'auth/weak-password': 'Password is too weak — use at least 6 characters.',
  'auth/too-many-requests':
    'Too many attempts. Please wait a moment and try again.',
  'auth/popup-closed-by-user': 'Sign-in was cancelled.',
  'auth/cancelled-popup-request': 'Sign-in was cancelled.',
  'auth/popup-blocked':
    'Your browser blocked the sign-in popup. Please allow popups and retry.',
  'auth/network-request-failed':
    'Network error. Please check your connection and try again.',
  'auth/requires-recent-login': 'Please sign in again to complete this action.',
  'auth/operation-not-allowed':
    'This sign-in method is not enabled. Please try another option or contact support.',
  'auth/unauthorized-continue-uri':
    'This link could not be sent for this site. Please contact support.',
  'auth/invalid-action-code':
    'This link is invalid or has already been used. Please request a new one.',
  'auth/expired-action-code':
    'This link has expired. Please request a new one.',
};

export function extractFirebaseAuthError(
  err: unknown,
  fallback = 'Something went wrong. Please try again.'
): string {
  const code = (err as { code?: string })?.code;
  // Only ever return our curated copy or the safe fallback — never the raw
  // Firebase/Error `message`, which can leak internal details to the UI.
  return MESSAGES[code ?? ''] || fallback;
}
